import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

export interface IterableAttribution {
  campaignId?: string;
  templateId?: string;
  entrySource?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

@Injectable({
  providedIn: 'root',
})
export class IterableAttributionService {
  private static readonly STORAGE_KEY = 'iterableAttribution';

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    this.captureAttributionFromUrl(window.location.href);

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.captureAttributionFromUrl(event.urlAfterRedirects);
      });
  }

  getAttribution(): IterableAttribution | null {
    if (!this.isBrowser) {
      return null;
    }

    const storedAttribution = sessionStorage.getItem(
      IterableAttributionService.STORAGE_KEY,
    );

    if (!storedAttribution) {
      return null;
    }

    try {
      return JSON.parse(storedAttribution) as IterableAttribution;
    } catch {
      sessionStorage.removeItem(IterableAttributionService.STORAGE_KEY);
      return null;
    }
  }

  clearAttribution(): void {
    if (!this.isBrowser) {
      return;
    }

    sessionStorage.removeItem(IterableAttributionService.STORAGE_KEY);
  }

  private captureAttributionFromUrl(url: string): void {
    const queryParams = this.getQueryParamsFromUrl(url);

    const campaignId = queryParams.get('campaignId') ?? undefined;
    const templateId = queryParams.get('templateId') ?? undefined;
    const entrySource = queryParams.get('entrySource') ?? undefined;

    const hasIterableAttribution =
      !!campaignId || !!templateId || !!entrySource;

    if (!hasIterableAttribution) {
      return;
    }

    const attribution = this.removeEmptyValues({
      campaignId,
      templateId,
      entrySource,
      utmSource: queryParams.get('utm_source') ?? undefined,
      utmMedium: queryParams.get('utm_medium') ?? undefined,
      utmCampaign: queryParams.get('utm_campaign') ?? undefined,
      utmContent: queryParams.get('utm_content') ?? undefined,
      utmTerm: queryParams.get('utm_term') ?? undefined,
    });

    sessionStorage.setItem(
      IterableAttributionService.STORAGE_KEY,
      JSON.stringify(attribution),
    );
  }

  private getQueryParamsFromUrl(url: string): URLSearchParams {
    try {
      return new URL(url, window.location.origin).searchParams;
    } catch {
      return new URLSearchParams();
    }
  }

  private removeEmptyValues(
    attribution: IterableAttribution,
  ): IterableAttribution {
    return Object.entries(attribution).reduce<IterableAttribution>(
      (cleanedAttribution, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          cleanedAttribution[key as keyof IterableAttribution] = value;
        }

        return cleanedAttribution;
      },
      {},
    );
  }
}
